/**
 * A machine's part on the 3D plan editor (`spec-onboarding-3d-plan-edit-machines`, R2.2 and R3.4;
 * `plan-machine-outline`, R1 to R4): its selection and hover on its model's outline, drawn by the marks
 * the plan's scene hands it (`PlanMachineOutlineMarks`), and its box, with that outline once the page's
 * store has worked it out, registered with its model for picking. Drawn inside the machine's own group,
 * so it turns and moves with it.
 */
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import type { Box3, BufferGeometry, Group, Mesh, Object3D } from 'three';
import type { Entity } from '@repo/frontend-types';
import { MachineRenderOrderContext } from './MachineMaterialContext';
import {
  PlanEditorMachineContext,
  type MachineOutlineEntry,
  type MachineOutlineSource,
  type PlanMachineBox,
  type PlanMachineSink,
} from './PlanEditorMachineContext';

/**
 * The box in the plan item's own frame. The group's x is the item's x and its z the item's −y, so
 * the footprint is the box the machine is drawn with.
 */
export const planMachineBox = (bounds: Box3): PlanMachineBox => ({
  width: bounds.max.x - bounds.min.x,
  depth: bounds.max.z - bounds.min.z,
  offsetX: (bounds.min.x + bounds.max.x) / 2,
  offsetY: -(bounds.min.z + bounds.max.z) / 2,
  top: bounds.max.y,
});

/**
 * The outline as the box carries it (R1.3, R4.2): mirrored as the model mesh is, and written in the
 * plan item's frame as `planMachineBox` writes the box (x, then −z). `undefined` while pending, `null`
 * for none.
 */
export const registeredOutline = (
  entry: MachineOutlineEntry,
  [mirrorX, mirrorZ]: readonly [number, number],
): PlanMachineBox['outline'] => {
  if (entry.state === 'pending') return undefined;
  if (entry.state === 'none') return null;
  return entry.loops.map(loop => loop.map(([x, z]) => ({ x: x * mirrorX, y: -(z * mirrorZ) })));
};

/** The model mesh beside this part in the machine's group: the one the ray is cast against. */
const modelBeside = (part: Object3D): Object3D | null =>
  part.parent?.children.find(
    (child): child is Mesh => child !== part && (child as Mesh).isMesh === true,
  ) ?? null;

const OUTLINE_NONE: MachineOutlineEntry = { state: 'none' };

/**
 * The geometry's outline from the page's store, requested once. Without a store, none: the machine is
 * drawn and picked by its box.
 */
const useMachineOutline = (
  outlines: MachineOutlineSource | undefined,
  geometry: BufferGeometry,
  modelName: string,
): MachineOutlineEntry => {
  const subscribe = useCallback(
    (listener: () => void) => (outlines ? outlines.subscribe(geometry, listener) : () => {}),
    [outlines, geometry],
  );
  const entry = useSyncExternalStore(subscribe, () =>
    outlines ? outlines.peek(geometry) : OUTLINE_NONE,
  );
  useEffect(() => {
    outlines?.request(geometry, modelName);
  }, [outlines, geometry, modelName]);
  return entry;
};

export interface PlanMachineRegistration {
  registry: PlanMachineSink | undefined;
  refid: string;
  hitBox: BufferGeometry;
  /** The drawn model, read as the registration runs (it is the part's sibling in the scene). */
  findModel: () => Object3D | null;
  outlines: MachineOutlineSource | undefined;
  /** The mesh the machine draws (its model's, or the placeholder's): what is outlined. */
  geometry: BufferGeometry;
  mirror: readonly [number, number];
  modelName: string;
}

/**
 * Registers the machine's box at once, its outline pending, and again with the outline when the store
 * settles (R4.2). The outline read is always the current geometry's, at the current mirror, so a result
 * for a model the machine no longer draws is never registered (Failure States, overtaken).
 */
export const usePlanMachineRegistration = ({
  registry,
  refid,
  hitBox,
  findModel,
  outlines,
  geometry,
  mirror,
  modelName,
}: PlanMachineRegistration): MachineOutlineEntry => {
  const entry = useMachineOutline(outlines, geometry, modelName);
  const [mirrorX, mirrorZ] = mirror;
  const outline = useMemo(
    () => registeredOutline(entry, [mirrorX, mirrorZ]),
    [entry, mirrorX, mirrorZ],
  );
  useLayoutEffect(() => {
    const model = findModel();
    if (!registry || !model || !hitBox.boundingBox) return undefined;
    registry.set(refid, { box: { ...planMachineBox(hitBox.boundingBox), outline }, model });
    return () => registry.remove(refid, model);
  }, [registry, refid, hitBox, outline, findModel]);
  return entry;
};

export const PlanEditorMachine = ({
  entity,
  hitBox,
  geometry,
  mirror,
  modelName,
}: {
  entity: Entity;
  hitBox: BufferGeometry;
  geometry: BufferGeometry;
  mirror: readonly [number, number];
  modelName: string;
}) => {
  const editor = useContext(PlanEditorMachineContext);
  const renderOrder = useContext(MachineRenderOrderContext);
  const partRef = useRef<Group>(null);
  const refid = entity.id;
  const findModel = useCallback(() => (partRef.current ? modelBeside(partRef.current) : null), []);
  const outline = usePlanMachineRegistration({
    registry: editor?.registry,
    refid,
    hitBox,
    findModel,
    outlines: editor?.outlines,
    geometry,
    mirror,
    modelName,
  });

  if (!editor) return null;
  const marks = editor.machineAppearance?.(refid) ?? {
    selected: editor.selectedIds.has(refid),
    hovered: !editor.selectedIds.has(refid) && editor.hoveredId === refid,
  };
  const { OutlineMarks } = editor;
  const order = editor.linesOrder ?? renderOrder;
  return (
    <group ref={partRef} renderOrder={order}>
      {OutlineMarks && (
        <OutlineMarks
          hitBox={hitBox}
          outline={outline}
          mirror={mirror}
          marks={marks}
          renderOrder={order}
        />
      )}
    </group>
  );
};
